import React, { useMemo } from "react";
import CountUp from "react-countup";
import BaseChart from "../../components/ui/BaseChart";
import BaseTooltip from "../../components/ui/BaseTooltip";

// Same card chrome, palette and sparkline treatment as the Work Order dashboard.
const CARDS = [
  {
    key: "totalWorkOrders",
    seriesKey: "total",
    label: "Total Work Orders",
    icon: "fa-solid fa-clipboard-list",
    color: "#0F40AE",
    bg: "#EEF2FF",
    help: "Total number of work orders across your entities scheduled to start within the selected date range. Excludes work orders in draft.",
  },
  {
    key: "completedWorkOrders",
    seriesKey: "completed",
    label: "Completed",
    icon: "fa-solid fa-circle-check",
    color: "#16A34A",
    bg: "#F0FDF4",
    help: "Work orders that have reached a terminal delivery state: Work Done, Verified, Approved, Invoiced or Paid.",
  },
  {
    key: "publishedWorkOrders",
    seriesKey: "published",
    label: "Accepted",
    icon: "fa-solid fa-bullhorn",
    color: "#0EA5E9",
    bg: "#F0F9FF",
    help: "Work orders accepted by your service provider and live on the marketplace, open to engineer offers, with no engineer allocated yet.",
  },
  {
    key: "assignedWorkOrders",
    seriesKey: "assigned",
    label: "Assigned",
    icon: "fa-solid fa-user-check",
    color: "#7C3AED",
    bg: "#F5F3FF",
    help: "Work orders an engineer has been assigned to but has not started yet. Counted on its own here, so these are not included in In Progress.",
  },
  {
    key: "inProgressWorkOrders",
    seriesKey: "inProgress",
    label: "In Progress",
    icon: "fa-jelly fa-regular fa-chart-bar",
    color: "#f7ad00",
    bg: "#ffedcdc1",
    help: "All the work orders currently checked in or checked out.",
  },
];

const Sparkline = ({ data, color }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 52,
        margin: [4, 0, 4, 0],
        backgroundColor: "transparent",
        animation: false,
      },
      title: { text: null },
      xAxis: { visible: false },
      yAxis: { visible: false, min: 0 },
      legend: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        area: {
          marker: { enabled: false },
          lineWidth: 2,
          lineColor: color,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${color}55`],
              [1, `${color}00`],
            ],
          },
        },
      },
      series: [{ data: data ?? [] }],
    }),
    [data, color],
  );

  if (!data?.length) return <div className="h-[52px]" />;
  return <BaseChart options={options} />;
};

const HelpIcon = ({ content }) => (
  <BaseTooltip content={<p className="text-white text-[11px] leading-relaxed">{content}</p>}>
    <i className="fa-light fa-circle-info text-gray-300 hover:text-gray-500 cursor-help text-sm transition-colors" />
  </BaseTooltip>
);

const KpiCards = ({ summary, trends = [], isLoading }) => (
  <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
    {CARDS.map((card) => (
      <div
        key={card.key}
        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3"
      >
        <div className="flex items-start justify-between">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: card.bg }}
          >
            <i className={`${card.icon} text-sm`} style={{ color: card.color }} />
          </div>
          <HelpIcon content={card.help} />
        </div>

        <div>
          <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide leading-none mb-1">
            {card.label}
          </p>
          <p className="text-3xl font-bold text-gray-800 leading-none">
            {isLoading ? (
              "—"
            ) : (
              <CountUp start={0} end={summary?.[card.key] ?? 0} duration={1.5} separator="," />
            )}
          </p>
        </div>

        <Sparkline data={trends.map((point) => point[card.seriesKey] ?? 0)} color={card.color} />
      </div>
    ))}

    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "#ECFEFF" }}
        >
          <i className="fa-solid fa-circle-half-stroke text-sm" style={{ color: "#0891B2" }} />
        </div>
        <HelpIcon
          content={`Completed work orders as a percentage of total work orders. Calculated as Completed \u00F7 Total \u00D7 100${
            summary?.totalWorkOrders
              ? ` (${summary.completedWorkOrders ?? 0} of ${summary.totalWorkOrders})`
              : ""
          }.`}
        />
      </div>

      <div>
        <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide leading-none mb-1">
          Completion Rate
        </p>
        <p className="text-3xl font-bold text-gray-800 leading-none">
          {isLoading ? (
            "—"
          ) : (
            <>
              <CountUp
                start={0}
                end={summary?.completionRate ?? 0}
                duration={1.5}
                decimals={1}
                decimal="."
              />
              <span className="text-xl">%</span>
            </>
          )}
        </p>
      </div>

      <div className="h-[52px] flex items-end pb-2">
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(summary?.completionRate ?? 0, 100)}%`,
              backgroundColor: "#0891B2",
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

export default KpiCards;
